import type { WeekStartDay } from '../locations/Location';

export interface TenantSettings {
  readonly timezone: string;
  readonly currency: string; // ISO 4217 currency codes (3 characters)
  readonly dateFormat: string;
  readonly weekStartDay: WeekStartDay; // Which day the business week starts on
  readonly features: readonly string[];
}

export type TenantStatus = 'active' | 'inactive' | 'suspended';
export type TenantPlan = 'starter' | 'professional' | 'enterprise';

export type TenantDeletionReason =
  | 'self_cancel'
  | 'ops_offboard'
  | 'nonpayment'
  | 'fraud_abuse'
  | 'ownership_transfer';

export interface ITenant {
  readonly tenantId: number;  // Professional ID starting from 10000001 (enterprise scale)
  readonly guid: string;      // GUID for external integrations
  readonly name: string;      // Business name
  readonly displayName: string; // Display name
  readonly status: TenantStatus;
  readonly plan: TenantPlan;
  readonly settings: TenantSettings;
  /** Set by suspendTenant(); UTC instant the tenant entered the suspended (soft-deleted) state. */
  readonly suspendedAt?: Date;
  /** suspendedAt + RETENTION_DAYS. The 5-plane purge refuses to run until now >= purgeAfter (unless force). */
  readonly purgeAfter?: Date;
  /** Stamped by tenantPurgeService once the irreversible 5-plane purge completes. Presence ⇒ already purged. */
  readonly purgedAt?: Date;
  /** Why the tenant was suspended (audit/legal). */
  readonly deletionReason?: TenantDeletionReason;
  /** uid (ops actor) or 'system:<job>' that initiated suspension. */
  readonly deletionActorUid?: string;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export interface ITenantData {
  tenantId: number;
  guid: string;
  name: string;
  displayName: string;
  status?: TenantStatus;
  plan?: TenantPlan;
  settings?: Partial<TenantSettings>;
  suspendedAt?: Date;
  purgeAfter?: Date;
  purgedAt?: Date;
  deletionReason?: TenantDeletionReason;
  deletionActorUid?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export class Tenant implements ITenant {
  readonly tenantId: number;  // Professional ID starting from 10000001 (enterprise scale)
  readonly guid: string;      // GUID for external integrations
  readonly name: string;      // Business name
  readonly displayName: string; // Display name
  readonly status: TenantStatus;
  readonly plan: TenantPlan;
  readonly settings: TenantSettings;
  readonly suspendedAt?: Date;
  readonly purgeAfter?: Date;
  readonly purgedAt?: Date;
  readonly deletionReason?: TenantDeletionReason;
  readonly deletionActorUid?: string;
  readonly createdAt: Date;
  readonly updatedAt: Date;

  constructor(data: ITenantData) {
    this.tenantId = data.tenantId;
    this.guid = data.guid;
    this.name = data.name;
    this.displayName = data.displayName;
    this.status = data.status ?? 'active';
    this.plan = data.plan ?? 'starter';
    this.settings = {
      timezone: data.settings?.timezone ?? 'UTC',
      currency: data.settings?.currency ?? 'USD',
      dateFormat: data.settings?.dateFormat ?? 'MM/DD/YYYY',
      weekStartDay: data.settings?.weekStartDay ?? 'monday',
      features: data.settings?.features ?? [],
    };
    this.suspendedAt = data.suspendedAt;
    this.purgeAfter = data.purgeAfter;
    this.purgedAt = data.purgedAt;
    this.deletionReason = data.deletionReason;
    this.deletionActorUid = data.deletionActorUid;
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  // Helper methods
  isActive(): boolean {
    return this.status === 'active';
  }

  isEnterprise(): boolean {
    return this.plan === 'enterprise';
  }

  isProfessional(): boolean {
    return this.plan === 'professional';
  }

  hasFeature(feature: string): boolean {
    return this.settings.features.includes(feature);
  }

  getDisplayName(): string {
    return this.displayName || this.name || 'Unknown Tenant';
  }

  canCreateLocations(): boolean {
    return this.plan === 'professional' || this.plan === 'enterprise';
  }

  getMaxLocations(): number {
    switch (this.plan) {
      case 'starter': return 1;
      case 'professional': return 5;
      case 'enterprise': return 999;
      default: return 1;
    }
  }

  // Serialization methods
  toJSON(): ITenant {
    return {
      tenantId: this.tenantId,
      guid: this.guid,
      name: this.name,
      displayName: this.displayName,
      status: this.status,
      plan: this.plan,
      settings: this.settings,
      suspendedAt: this.suspendedAt,
      purgeAfter: this.purgeAfter,
      purgedAt: this.purgedAt,
      deletionReason: this.deletionReason,
      deletionActorUid: this.deletionActorUid,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  static fromJSON(data: Record<string, any>): Tenant {
    if (!data.tenantId || typeof data.tenantId !== 'number') throw new Error("Tenant.fromJSON: tenantId is missing or invalid.");
    if (!data.guid || typeof data.guid !== 'string') throw new Error("Tenant.fromJSON: guid is missing or invalid.");
    if (!data.name || typeof data.name !== 'string') throw new Error("Tenant.fromJSON: name is missing or invalid.");
    if (!data.displayName || typeof data.displayName !== 'string') throw new Error("Tenant.fromJSON: displayName is missing or invalid.");
    
    // Helper function to convert Firestore timestamps to Date objects
    const convertTimestamp = (timestamp: any): Date | undefined => {
      if (!timestamp) return undefined;
      // Check if it's a Firestore Timestamp object
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      // Check if it's already a Date object
      if (timestamp instanceof Date) {
        return timestamp;
      }
      // Try to parse as string/number
      return new Date(timestamp);
    };
    
    return new Tenant({
      tenantId: data.tenantId,
      guid: data.guid,
      name: data.name,
      displayName: data.displayName,
      status: data.status as TenantStatus,
      plan: data.plan as TenantPlan,
      settings: data.settings as Partial<TenantSettings>,
      suspendedAt: convertTimestamp(data.suspendedAt),
      purgeAfter: convertTimestamp(data.purgeAfter),
      purgedAt: convertTimestamp(data.purgedAt),
      deletionReason: data.deletionReason as TenantDeletionReason | undefined,
      deletionActorUid: typeof data.deletionActorUid === 'string' ? data.deletionActorUid : undefined,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    });
  }
} 